import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { marcoApi, libraryApi } from './api';
import { MARCO_PAGES, PAGE_CATEGORIES } from './config';
import { ASSETS } from '../../config/assets';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Check, ArrowLeft, ArrowRight, ChevronUp, ChevronDown, X,
  FileText, Eye, Save, Layers, GripVertical, Lock, Unlock,
  Download, Copy, Trash2, Link2, Calendar, Shield,
  ChevronRight, LayoutGrid, Presentation, BarChart3, Minus, Plus,
  FolderOpen, Image, Upload, User, Pencil, CopyPlus,
} from 'lucide-react';

const STEPS = [
  { id: 'select', en: 'Select', fr: 'Selectionner' },
  { id: 'arrange', en: 'Arrange', fr: 'Organiser' },
  { id: 'compose', en: 'Compose', fr: 'Composer' },
  { id: 'security', en: 'Security', fr: 'Securite' },
  { id: 'preview', en: 'Preview', fr: 'Apercu' },
  { id: 'confirm', en: 'Generate', fr: 'Generer' },
];

const COMPOSITION_MODES = [
  {
    id: 'detailed',
    en: 'Detailed',
    fr: 'Detaille',
    descEn: 'One slide per selected page. Full screenshot layout.',
    descFr: 'Une diapositive par page. Capture pleine page.',
    icon: LayoutGrid,
  },
  {
    id: 'condensed',
    en: 'Condensed',
    fr: 'Condense',
    descEn: 'Group related pages into fewer slides. Grid layout with captions.',
    descFr: 'Regrouper les pages en moins de diapositives. Grille avec legendes.',
    icon: Presentation,
  },
  {
    id: 'executive',
    en: 'Executive',
    fr: 'Executif',
    descEn: 'High-level summary with key visuals. Decision-maker focused.',
    descFr: 'Resume avec visuels cles. Oriente decideurs.',
    icon: BarChart3,
  },
];

const MarcoCreate = () => {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const t = (en, fr) => language === 'fr' ? fr : en;

  // Step state
  const [step, setStep] = useState(0);

  // Step 1: Selection
  const [selectedIds, setSelectedIds] = useState([]);
  const [collapsedSections, setCollapsedSections] = useState({});

  // Step 2: Arrangement
  const [orderedPages, setOrderedPages] = useState([]);
  const [dragIdx, setDragIdx] = useState(null);

  // Step 3: Compose
  const [compositionMode, setCompositionMode] = useState('detailed');
  const [customSlideCount, setCustomSlideCount] = useState(null);
  const [libraryAssets, setLibraryAssets] = useState([]);
  const [showLibraryPicker, setShowLibraryPicker] = useState(false);
  const [librarySlideMode, setLibrarySlideMode] = useState('visual');
  const [librarySlides, setLibrarySlides] = useState([]);
  const [pickerFolders, setPickerFolders] = useState([]);
  const [pickerFolderId, setPickerFolderId] = useState(null); // null = root

  // Personalization
  const [showPersonalize, setShowPersonalize] = useState(false);
  const [clientName, setClientName] = useState('');
  const [clientLogoFile, setClientLogoFile] = useState(null);
  const [clientLogoPreview, setClientLogoPreview] = useState('');
  const [clientLogoId, setClientLogoId] = useState('');
  const [brandColor, setBrandColor] = useState('');
  const [locationName, setLocationName] = useState('');
  const [preparedFor, setPreparedFor] = useState('');
  const [uploadingLogo, setUploadingLogo] = useState(false);

  const isPersonalized = clientName || clientLogoId || brandColor || locationName || preparedFor;

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setClientLogoFile(file);
    setClientLogoPreview(URL.createObjectURL(file));
    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('title', `Client Logo - ${clientName || 'Custom'}`);
      formData.append('category', 'client-logos');
      const result = await libraryApi.upload(formData);
      setClientLogoId(result.id);
    } catch (err) {
      console.error('Logo upload failed:', err);
    } finally {
      setUploadingLogo(false);
    }
  };

  const resetPersonalization = () => {
    setClientName('');
    setClientLogoFile(null);
    setClientLogoPreview('');
    setClientLogoId('');
    setBrandColor('');
    setLocationName('');
    setPreparedFor('');
    setShowPersonalize(false);
  };

  // Step 4: Security
  const [usePassword, setUsePassword] = useState(true);
  const [customPassword, setCustomPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [passwordExpiry, setPasswordExpiry] = useState('');

  // Step 5: Generate / Confirm
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [createdMarco, setCreatedMarco] = useState(null);
  const [copied, setCopied] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState('');

  const [error, setError] = useState('');

  // ─── Duplicate Pre-fill ─────────────────────────
  useEffect(() => {
    const dup = location.state?.duplicateFrom;
    if (!dup) return;
    
    // Reset createdMarco state when duplicating (important for same-page navigation)
    setCreatedMarco(null);
    
    // Pre-fill pages
    const pages = (dup.pages || []).sort((a, b) => a.order - b.order);
    const ids = pages.map(p => p.page_id);
    setSelectedIds(ids);
    setOrderedPages(ids.map(id => MARCO_PAGES.find(p => p.id === id)).filter(Boolean));
    // Pre-fill compose settings
    setCompositionMode(dup.composition_mode || 'detailed');
    setCustomSlideCount(dup.slide_count || null);
    // Pre-fill library slides
    if (dup.library_slides?.length) {
      setLibrarySlides(dup.library_slides.map(ls => ({
        asset: { id: ls.asset_id, file_url: ls.file_url || '', title: ls.title, file_type: 'png' },
        mode: ls.mode || 'visual',
        title: ls.title || '',
        description: ls.description || '',
      })));
    } else {
      setLibrarySlides([]);
    }
    // Pre-fill personalization
    const p = dup.personalization;
    if (p) {
      setClientName(p.client_name || '');
      setClientLogoId(p.client_logo_id || '');
      setBrandColor(p.brand_color || '');
      setLocationName(p.location_name || '');
      setPreparedFor(p.prepared_for || '');
      if (p.client_name || p.client_logo_id || p.brand_color || p.location_name || p.prepared_for) {
        setShowPersonalize(true);
      }
      // Restore logo preview if logo exists in library
      if (p.client_logo_id) {
        setClientLogoPreview(libraryApi.fileUrl(p.client_logo_id));
      }
    } else {
      // Reset personalization if original had none
      setClientName('');
      setClientLogoId('');
      setBrandColor('');
      setLocationName('');
      setPreparedFor('');
      setShowPersonalize(false);
      setClientLogoPreview('');
    }
    // Pre-fill name and jump to Compose step
    setName(`Copy of ${dup.name || 'Marcom'}`);
    setStep(2);
    // Reset security settings (password not inherited)
    setUsePassword(true);
    setCustomPassword('');
    setConfirmPassword('');
    setPasswordExpiry('');
    // Load library assets for the picker
    libraryApi.list().then(setLibraryAssets).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state?.duplicateFrom]);

  // ─── Step 1: Page Selection ────────────────────

  const togglePage = (pageId) => {
    setSelectedIds(prev =>
      prev.includes(pageId)
        ? prev.filter(id => id !== pageId)
        : [...prev, pageId]
    );
  };

  const toggleSection = (sectionId) => {
    setCollapsedSections(prev => ({ ...prev, [sectionId]: !prev[sectionId] }));
  };

  const goToArrange = () => {
    if (selectedIds.length === 0) {
      setError(t('Select at least one page', 'Selectionnez au moins une page'));
      return;
    }
    setError('');
    const pages = selectedIds.map(id => MARCO_PAGES.find(p => p.id === id)).filter(Boolean);
    setOrderedPages(pages);
    setStep(1);
  };

  // ─── Step 2: Arrange ───────────────────────────

  const moveUp = (idx) => {
    if (idx === 0) return;
    setOrderedPages(prev => {
      const arr = [...prev];
      [arr[idx - 1], arr[idx]] = [arr[idx], arr[idx - 1]];
      return arr;
    });
  };

  const moveDown = (idx) => {
    if (idx >= orderedPages.length - 1) return;
    setOrderedPages(prev => {
      const arr = [...prev];
      [arr[idx], arr[idx + 1]] = [arr[idx + 1], arr[idx]];
      return arr;
    });
  };

  const removePage = (idx) => {
    const removed = orderedPages[idx];
    setOrderedPages(prev => prev.filter((_, i) => i !== idx));
    setSelectedIds(prev => prev.filter(id => id !== removed.id));
  };

  const handleDragStart = (idx) => setDragIdx(idx);
  const handleDragOver = (e, idx) => {
    e.preventDefault();
    if (dragIdx === null || dragIdx === idx) return;
    setOrderedPages(prev => {
      const arr = [...prev];
      const item = arr.splice(dragIdx, 1)[0];
      arr.splice(idx, 0, item);
      return arr;
    });
    setDragIdx(idx);
  };
  const handleDragEnd = () => setDragIdx(null);

  const goToCompose = async () => {
    if (orderedPages.length === 0) return;
    setError('');
    setCustomSlideCount(null);
    // Load library assets for the picker
    try {
      const [assets, folders] = await Promise.all([
        libraryApi.list(''),
        libraryApi.listFolders(),
      ]);
      setLibraryAssets(assets);
      setPickerFolders(folders);
      setPickerFolderId(null);
    } catch (err) {
      // Non-blocking: library just won't show assets
    }
    setStep(2);
  };

  // ─── Step 3: Compose ───────────────────────────

  const getOutputSlideCount = () => {
    const n = orderedPages.length;
    if (customSlideCount) return customSlideCount;
    if (compositionMode === 'detailed') return n;
    if (compositionMode === 'condensed') return Math.max(1, Math.ceil(n / 3));
    if (compositionMode === 'executive') return Math.max(1, Math.ceil(n / 4));
    return n;
  };

  const getSlideGroups = () => {
    const slideCount = getOutputSlideCount();
    const pages = [...orderedPages];
    if (compositionMode === 'detailed' && !customSlideCount) {
      return pages.map(p => [p]);
    }
    // Distribute pages across the target slide count
    const groups = Array.from({ length: slideCount }, () => []);
    pages.forEach((p, i) => {
      groups[i % slideCount].push(p);
    });
    return groups;
  };

  const goToSecurity = () => {
    setError('');
    setStep(3);
  };

  const addLibrarySlide = (asset) => {
    setLibrarySlides(prev => [...prev, {
      asset,
      mode: librarySlideMode,
      title: asset.title,
      description: asset.description || '',
    }]);
    setShowLibraryPicker(false);
  };

  const removeLibrarySlide = (idx) => {
    setLibrarySlides(prev => prev.filter((_, i) => i !== idx));
  };

  // ─── Step 4: Security ──────────────────────────

  const goToPreview = () => {
    if (usePassword && customPassword) {
      if (customPassword.length < 4) {
        setError(t('Password must be at least 4 characters', 'Le mot de passe doit contenir au moins 4 caracteres'));
        return;
      }
      if (customPassword !== confirmPassword) {
        setError(t('Passwords do not match', 'Les mots de passe ne correspondent pas'));
        return;
      }
    }
    setError('');
    setStep(4);
  };

  const goToGenerate = () => {
    setStep(5);
  };

  // ─── Step 6: Generate & Confirm ────────────────

  const handleGenerate = async () => {
    if (!name.trim()) {
      setError(t('Please enter a name', 'Veuillez entrer un nom'));
      return;
    }
    setError('');
    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        pages: orderedPages.map((p, i) => ({
          page_id: p.id,
          title_en: p.titleEn,
          title_fr: p.titleFr,
          order: i,
        })),
        library_slides: librarySlides.map((ls, i) => ({
          asset_id: ls.asset.id,
          title: ls.title,
          description: ls.description,
          mode: ls.mode,
          file_url: ls.asset.file_url,
          order: i,
        })),
        use_password: usePassword,
        custom_password: usePassword ? (customPassword || null) : null,
        password_expiry: passwordExpiry || null,
        composition_mode: compositionMode,
        slide_count: customSlideCount || null,
        personalization: isPersonalized ? {
          client_name: clientName || null,
          client_logo_id: clientLogoId || null,
          brand_color: brandColor || null,
          location_name: locationName || null,
          prepared_for: preparedFor || null,
        } : null,
      };
      const result = await marcoApi.create(payload);
      setCreatedMarco(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDownload = async () => {
    if (!createdMarco) return;
    setDownloading(true);
    try {
      const API = '';
      const res = await fetch(`${API}/api/marcom/${createdMarco.id}/pdf?lang=${language}`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Download failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${createdMarco.name}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
    } finally {
      setDownloading(false);
    }
  };

  const handleCopyLink = () => {
    const link = `${window.location.origin}/marco/view/${createdMarco.id}`;
    navigator.clipboard.writeText(link);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDelete = async () => {
    if (!createdMarco) return;
    await marcoApi.delete(createdMarco.id);
    navigate('/marcom');
  };

  const handleRename = async () => {
    if (!createdMarco || !renameValue.trim()) return;
    try {
      const updated = await marcoApi.rename(createdMarco.id, renameValue.trim());
      setCreatedMarco(updated);
      setRenaming(false);
    } catch (err) {
      console.error('Rename failed:', err);
    }
  };

  const handleDuplicate = () => {
    if (!createdMarco) return;
    navigate('/marcom/create', { state: { duplicateFrom: createdMarco } });
  };

  // ─── Grouped Pages ─────────────────────────────

  const grouped = Object.entries(PAGE_CATEGORIES)
    .map(([catId, cat]) => ({
      id: catId,
      label: language === 'fr' ? cat.labelFr : cat.labelEn,
      pages: MARCO_PAGES.filter(p => p.category === catId),
    }))
    .filter(g => g.pages.length > 0);

  // ─── Preview Data ──────────────────────────────

  const previewPages = step === 0
    ? selectedIds.map(id => MARCO_PAGES.find(p => p.id === id)).filter(Boolean)
    : orderedPages;

  // ─── Preview Panel ─────────────────────────────

  const PreviewPanel = ({ pages, showComposition }) => {
    const groups = showComposition ? getSlideGroups() : null;
    const displaySlideCount = showComposition ? getOutputSlideCount() : pages.length;
    const modeLabel = showComposition
      ? (COMPOSITION_MODES.find(m => m.id === compositionMode)?.[language === 'fr' ? 'fr' : 'en'] || '')
      : '';

    // Resolve preview branding
    const previewLogo = clientLogoPreview || ASSETS.LOGO;
    const accentStyle = brandColor ? { backgroundColor: brandColor } : undefined;
    const badgeClass = brandColor ? '' : 'bg-wine/90';

    return (
      <div className="bg-[#111] border border-white/10 rounded-xl overflow-hidden">
        <div className="px-4 py-2.5 border-b border-white/5 flex items-center justify-between">
          <span className="text-xs font-bold uppercase tracking-[0.15em] text-white/30 flex items-center gap-1.5">
            <Eye className="w-3.5 h-3.5" strokeWidth={1.5} />
            {t('Live Preview', 'Apercu en Direct')}
          </span>
          <span className="text-xs text-white/20">
            {showComposition
              ? `${displaySlideCount} ${t('slides', 'diapositives')}`
              : `${pages.length} ${t('pages', 'pages')}`
            }
          </span>
        </div>
        {showComposition && modeLabel && (
          <div className={`px-4 py-1.5 border-b border-white/5 ${brandColor ? '' : 'bg-wine/5 border-wine/10'}`}
            style={brandColor ? { backgroundColor: `${brandColor}10`, borderColor: `${brandColor}20` } : undefined}>
            <span className={`text-[10px] font-bold uppercase tracking-[0.12em] ${brandColor ? '' : 'text-wine/70'}`}
              style={brandColor ? { color: `${brandColor}BB` } : undefined}>
              {modeLabel}
            </span>
          </div>
        )}
        {/* Personalization banner when active */}
        {isPersonalized && (
          <div data-testid="preview-brand-banner" className="px-4 py-2 border-b border-white/5 flex items-center gap-2">
            {clientLogoPreview && (
              <img src={clientLogoPreview} alt="" className="h-5 w-auto object-contain" />
            )}
            <span className="text-[10px] font-semibold text-white/50 uppercase tracking-wider truncate">
              {clientName || t('Custom Branding', 'Image Personnalisee')}
            </span>
            {preparedFor && (
              <span className="text-[10px] text-white/25 ml-auto truncate">
                {t('For', 'Pour')}: {preparedFor}
              </span>
            )}
          </div>
        )}
        <div className="p-3 space-y-2 max-h-[520px] overflow-y-auto">
          {pages.length === 0 ? (
            <div className="text-center py-10 text-white/20 text-sm">
              {t('Select pages to preview', 'Selectionnez des pages pour l\'apercu')}
            </div>
          ) : showComposition && groups ? (
            /* Composition-aware preview */
            groups.map((group, slideIdx) => (
              <div
                key={slideIdx}
                className="relative bg-[#0D0D0D] border border-white/5 rounded-lg overflow-hidden"
              >
                {/* Slide number badge with brand color */}
                <div className={`absolute top-1.5 left-1.5 z-10 ${badgeClass} backdrop-blur-sm text-white text-[10px] font-bold w-5 h-5 rounded-md flex items-center justify-center`}
                  style={accentStyle}>
                  {slideIdx + 1}
                </div>
                {group.length === 1 ? (
                  /* Single page slide */
                  <>
                    <div className="relative aspect-[16/10] overflow-hidden">
                      <img src={group[0].thumbnail} alt="" className="w-full h-full object-cover object-top" />
                      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <img src={previewLogo} alt="" className="w-12 h-auto opacity-[0.08]" />
                      </div>
                    </div>
                    <div className="px-2.5 py-1.5">
                      <h4 className="font-heading text-xs font-medium text-white truncate">
                        {language === 'fr' ? group[0].titleFr : group[0].titleEn}
                      </h4>
                    </div>
                  </>
                ) : (
                  /* Multi-page slide (grid) */
                  <>
                    <div className={`grid ${group.length <= 2 ? 'grid-cols-2' : 'grid-cols-3'} gap-px bg-white/5`}>
                      {group.map((page) => (
                        <div key={page.id} className="relative aspect-[16/10] overflow-hidden bg-[#111]">
                          <img src={page.thumbnail} alt="" className="w-full h-full object-cover object-top" />
                        </div>
                      ))}
                    </div>
                    <div className="px-2.5 py-1.5">
                      <h4 className="font-heading text-[10px] font-medium text-white/60 truncate">
                        {group.map(p => language === 'fr' ? p.titleFr : p.titleEn).join(' + ')}
                      </h4>
                    </div>
                  </>
                )}
              </div>
            ))
          ) : (
            /* Standard per-page preview */
            pages.map((page, idx) => (
              <div key={page.id} className="relative bg-[#0D0D0D] border border-white/5 rounded-lg overflow-hidden">
                <div className="relative aspect-[16/10] overflow-hidden">
                  <img src={page.thumbnail} alt={language === 'fr' ? page.titleFr : page.titleEn} className="w-full h-full object-cover object-top" />
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <img src={previewLogo} alt="" className="w-12 h-auto opacity-[0.08]" />
                  </div>
                  <div className={`absolute top-1.5 left-1.5 ${badgeClass} backdrop-blur-sm text-white text-[10px] font-bold w-5 h-5 rounded-md flex items-center justify-center`}
                    style={accentStyle}>
                    {idx + 1}
                  </div>
                </div>
                <div className="px-2.5 py-1.5 bg-[#0D0D0D]">
                  <h4 className="font-heading text-xs font-medium text-white truncate">
                    {language === 'fr' ? page.titleFr : page.titleEn}
                  </h4>
                </div>
              </div>
            ))
          )}
        </div>
        <div className="border-t border-white/5 px-4 py-2 flex items-center justify-between">
          {clientLogoPreview ? (
            <img src={clientLogoPreview} alt="" className="h-5 w-auto opacity-60" />
          ) : (
            <span className="text-[10px] text-white/15">Marcom by YUL On Tap</span>
          )}
          <span className="text-[10px] text-white/20 truncate ml-2">
            {clientName || (isPersonalized ? '' : 'yulontap.com')}
          </span>
        </div>
      </div>
    );
  };

  // ─── If Marco created, show confirmation ───────

  if (createdMarco) {
    return (
      <div data-testid="marco-created" className="min-h-screen bg-[#0D0D0D]">
        <header className="border-b border-white/10 bg-[#0D0D0D]/95 backdrop-blur-md sticky top-0 z-50">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 h-16 flex items-center">
            <Link to="/marco" className="flex items-center gap-3">
              <img src={ASSETS.LOGO} alt="YUL On Tap" className="h-8 w-auto" />
              <div className="h-5 w-px bg-white/10" />
              <span className="font-heading text-lg font-semibold text-white">Marcom</span>
            </Link>
          </div>
        </header>

        <main className="max-w-lg mx-auto px-4 sm:px-6 py-12">
          <div className="text-center mb-8">
            <div className="w-20 h-20 bg-green-500/10 border border-green-500/20 rounded-2xl flex items-center justify-center mx-auto mb-5">
              <Check className="w-10 h-10 text-green-400" strokeWidth={1.5} />
            </div>
            <h1 className="font-heading text-3xl font-semibold text-white mb-2">
              {t('Marcom Created', 'Marcom Cree')}
            </h1>
            <p className="text-white/50">
              {t(
                `"${createdMarco.name}" is ready with ${createdMarco.page_count} pages.`,
                `"${createdMarco.name}" est pret avec ${createdMarco.page_count} pages.`
              )}
            </p>
          </div>

          <div className="bg-[#1A1A1A] border border-white/10 rounded-xl divide-y divide-white/5">
            {createdMarco.use_password && createdMarco.password && (
              <div className="p-4 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Lock className="w-4 h-4 text-wine" strokeWidth={1.5} />
                  <span className="text-white/50 text-sm">{t('PDF Password', 'Mot de passe PDF')}</span>
                </div>
                <code className="text-white font-mono text-sm bg-white/5 px-3 py-1 rounded-lg">
                  {createdMarco.password}
                </code>
              </div>
            )}

            <div className="p-4">
              <Button
                onClick={handleDownload}
                disabled={downloading}
                data-testid="download-created-pdf"
                className="w-full bg-wine hover:bg-wine-light text-white rounded-xl h-12 text-base font-medium"
              >
                {downloading ? (
                  <span className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    {t('Generating PDF...', 'Generation du PDF...')}
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Download className="w-5 h-5" strokeWidth={1.5} />
                    {t('Download PDF', 'Telecharger le PDF')}
                  </span>
                )}
              </Button>
            </div>

            <div className="p-4">
              {renaming ? (
                <div className="flex items-center gap-2">
                  <Input
                    value={renameValue}
                    onChange={(e) => setRenameValue(e.target.value)}
                    data-testid="rename-input"
                    placeholder={t('New name...', 'Nouveau nom...')}
                    className="flex-1 bg-white/5 border-white/10 text-white placeholder:text-white/25 h-10 rounded-lg"
                    onKeyDown={(e) => e.key === 'Enter' && handleRename()}
                    autoFocus
                  />
                  <Button onClick={handleRename} data-testid="rename-confirm"
                    className="bg-wine hover:bg-wine-light text-white rounded-lg h-10 px-4 text-sm">
                    {t('Save', 'Sauvegarder')}
                  </Button>
                  <Button onClick={() => setRenaming(false)} variant="ghost"
                    className="text-white/40 hover:text-white h-10 px-3 text-sm">
                    {t('Cancel', 'Annuler')}
                  </Button>
                </div>
              ) : (
                <Button
                  onClick={() => { setRenaming(true); setRenameValue(createdMarco.name); }}
                  variant="outline"
                  data-testid="rename-btn"
                  className="w-full bg-transparent text-white border-white/20 hover:bg-white/5 rounded-xl h-11"
                >
                  <span className="flex items-center gap-2">
                    <Pencil className="w-4 h-4" strokeWidth={1.5} />
                    {t('Rename', 'Renommer')}
                  </span>
                </Button>
              )}
            </div>

            <div className="p-4">
              <Button
                onClick={handleCopyLink}
                variant="outline"
                data-testid="copy-link-btn"
                className="w-full bg-transparent text-white border-white/20 hover:bg-white/5 rounded-xl h-11"
              >
                <span className="flex items-center gap-2">
                  {copied ? (
                    <><Check className="w-4 h-4 text-green-400" strokeWidth={2} /><span className="text-green-400">{t('Link Copied!', 'Lien Copie!')}</span></>
                  ) : (
                    <><Link2 className="w-4 h-4" strokeWidth={1.5} />{t('Copy Share Link', 'Copier le Lien de Partage')}</>
                  )}
                </span>
              </Button>
            </div>

            <div className="p-4">
              <Button
                onClick={handleDuplicate}
                variant="outline"
                data-testid="duplicate-btn"
                className="w-full bg-transparent text-white border-white/20 hover:bg-white/5 rounded-xl h-11"
              >
                <span className="flex items-center gap-2">
                  <CopyPlus className="w-4 h-4" strokeWidth={1.5} />
                  {t('Duplicate', 'Dupliquer')}
                </span>
              </Button>
            </div>

            <div className="p-4">
              {confirmDelete ? (
                <div className="flex items-center gap-3">
                  <Button onClick={handleDelete} data-testid="confirm-delete-created"
                    className="flex-1 bg-red-600 hover:bg-red-700 text-white rounded-xl h-10 text-sm">
                    {t('Yes, Delete', 'Oui, Supprimer')}
                  </Button>
                  <Button onClick={() => setConfirmDelete(false)} variant="ghost"
                    className="flex-1 text-white/50 hover:text-white h-10 text-sm">
                    {t('Cancel', 'Annuler')}
                  </Button>
                </div>
              ) : (
                <button onClick={() => setConfirmDelete(true)} data-testid="delete-created-btn"
                  className="w-full flex items-center justify-center gap-2 text-white/30 hover:text-red-400 py-2 text-sm transition-colors">
                  <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                  {t('Delete This Marcom', 'Supprimer ce Marcom')}
                </button>
              )}
            </div>
          </div>

          <div className="mt-6 text-center">
            <Link to="/marco" className="text-wine hover:text-wine-light text-sm font-medium transition-colors">
              {t('Back to Dashboard', 'Retour au Tableau de Bord')}
            </Link>
          </div>
        </main>
      </div>
    );
  }

  // ─── Render ────────────────────────────────────

  return (
    <div data-testid="marco-create" className="min-h-screen bg-[#0D0D0D]">
      {/* Header */}
      <header className="border-b border-white/10 bg-[#0D0D0D]/95 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => step === 0 ? navigate('/marcom') : setStep(step - 1)}
              data-testid="back-btn"
              className="p-2 text-white/50 hover:text-white hover:bg-white/5 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5" strokeWidth={1.5} />
            </button>
            <span className="font-heading text-lg font-semibold text-white">
              {t('New Marcom', 'Nouveau Marcom')}
            </span>
          </div>

          {/* Step indicators */}
          <div className="flex items-center gap-1">
            {STEPS.map((s, i) => (
              <div key={s.id} className="flex items-center">
                <div className={`flex items-center gap-1 px-2 py-1.5 rounded-full text-xs font-medium transition-all ${
                  i === step ? 'bg-wine text-white' :
                  i < step ? 'bg-wine/20 text-wine' :
                  'bg-white/5 text-white/25'
                }`}>
                  {i < step ? <Check className="w-3 h-3" strokeWidth={2.5} /> : null}
                  <span className="hidden sm:inline">{language === 'fr' ? s.fr : s.en}</span>
                  <span className="sm:hidden">{i + 1}</span>
                </div>
                {i < STEPS.length - 1 && <div className="w-3 h-px bg-white/10 mx-0.5" />}
              </div>
            ))}
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-8">

        {/* ═══ STEP 1: Select Pages ═══ */}
        {step === 0 && (
          <div data-testid="step-select">
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
              <div className="lg:col-span-3">
                <div className="mb-5">
                  <h2 className="font-heading text-2xl font-semibold text-white mb-1">
                    {t('Choose Pages', 'Choisir les Pages')}
                  </h2>
                  <p className="text-white/40 text-sm">
                    {t('Tap to select pages for your Marcom.', 'Appuyez pour selectionner les pages.')}
                  </p>
                </div>

                {selectedIds.length > 0 && (
                  <div className="mb-4 inline-flex items-center gap-2 px-3 py-1.5 bg-green-500/10 border border-green-500/20 rounded-full">
                    <Layers className="w-3.5 h-3.5 text-green-400" strokeWidth={1.5} />
                    <span className="text-xs text-green-400 font-bold">
                      {selectedIds.length} {t('selected', 'selectionne(s)')}
                    </span>
                  </div>
                )}

                {grouped.map((group) => {
                  const isCollapsed = collapsedSections[group.id];
                  const selectedInGroup = group.pages.filter(p => selectedIds.includes(p.id)).length;
                  return (
                    <div key={group.id} className="mb-4">
                      {/* Collapsible section header */}
                      <button
                        onClick={() => toggleSection(group.id)}
                        data-testid={`section-toggle-${group.id}`}
                        className="w-full flex items-center justify-between py-2 px-1 group/section"
                      >
                        <div className="flex items-center gap-2">
                          <ChevronRight className={`w-3.5 h-3.5 text-white/25 transition-transform ${isCollapsed ? '' : 'rotate-90'}`} strokeWidth={2} />
                          <h3 className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/25 group-hover/section:text-white/40 transition-colors">
                            {group.label}
                          </h3>
                        </div>
                        <div className="flex items-center gap-2">
                          {selectedInGroup > 0 && (
                            <span className="text-[10px] font-semibold text-wine bg-wine/10 px-2 py-0.5 rounded-full">
                              {selectedInGroup}
                            </span>
                          )}
                          <span className="text-[10px] text-white/15">{group.pages.length}</span>
                        </div>
                      </button>

                      {/* Section content */}
                      {!isCollapsed && (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-1">
                          {group.pages.map((page) => {
                            const isSelected = selectedIds.includes(page.id);
                            return (
                              <button
                                key={page.id}
                                onClick={() => togglePage(page.id)}
                                data-testid={`page-card-${page.id}`}
                                className={`relative text-left rounded-xl border overflow-hidden transition-all duration-150 ${
                                  isSelected
                                    ? 'border-wine/40 ring-1 ring-wine/20'
                                    : 'border-white/8 hover:border-white/15'
                                }`}
                              >
                                <div className="relative aspect-[16/9] overflow-hidden bg-[#111]">
                                  <img src={page.thumbnail} alt={language === 'fr' ? page.titleFr : page.titleEn}
                                    className="w-full h-full object-cover object-top" loading="lazy" />
                                  {isSelected && <div className="absolute inset-0 bg-wine/30" />}
                                  <div className={`absolute top-2 right-2 w-6 h-6 rounded-full flex items-center justify-center transition-all ${
                                    isSelected ? 'bg-wine shadow-lg' : 'bg-black/40 backdrop-blur-sm'
                                  }`}>
                                    {isSelected && <Check className="w-3.5 h-3.5 text-white" strokeWidth={2.5} />}
                                  </div>
                                </div>
                                <div className={`px-3 py-2.5 ${isSelected ? 'bg-wine/10' : 'bg-[#1A1A1A]'}`}>
                                  <h4 className="font-heading text-sm font-medium text-white truncate">
                                    {language === 'fr' ? page.titleFr : page.titleEn}
                                  </h4>
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              <div className="lg:col-span-2">
                <div className="sticky top-24">
                  <PreviewPanel pages={previewPages} showComposition={false} />
                </div>
              </div>
            </div>

            {error && <p data-testid="select-error" className="text-red-400 text-sm mt-4">{error}</p>}

            <div className="flex justify-end mt-6">
              <Button onClick={goToArrange} data-testid="next-to-arrange"
                className="bg-wine hover:bg-wine-light text-white rounded-xl px-6 h-11">
                {t('Next', 'Suivant')} <ArrowRight className="w-4 h-4 ml-2" strokeWidth={1.5} />
              </Button>
            </div>
          </div>
        )}

        {/* ═══ STEP 2: Arrange ═══ */}
        {step === 1 && (
          <div data-testid="step-arrange">
            <div className="mb-5">
              <h2 className="font-heading text-2xl font-semibold text-white mb-1">
                {t('Arrange Pages', 'Organiser les Pages')}
              </h2>
              <p className="text-white/40 text-sm">
                {t('Drag to reorder. This is how they appear in the Marcom.', 'Glissez pour reorganiser.')}
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
              <div className="lg:col-span-3">
                <div className="space-y-1.5">
                  {orderedPages.map((page, idx) => (
                    <div
                      key={page.id} draggable
                      onDragStart={() => handleDragStart(idx)}
                      onDragOver={(e) => handleDragOver(e, idx)}
                      onDragEnd={handleDragEnd}
                      data-testid={`ordered-page-${idx}`}
                      className={`flex items-center gap-3 bg-[#1A1A1A] border rounded-xl p-2 group cursor-grab active:cursor-grabbing transition-all ${
                        dragIdx === idx ? 'border-wine/40 bg-wine/5 scale-[0.98]' : 'border-white/8'
                      }`}
                    >
                      <div className="text-white/15 cursor-grab">
                        <GripVertical className="w-4 h-4" strokeWidth={1.5} />
                      </div>
                      <span className="w-7 h-7 bg-wine/15 rounded-lg flex items-center justify-center text-wine text-xs font-semibold flex-shrink-0">
                        {idx + 1}
                      </span>
                      <div className="w-16 h-9 rounded overflow-hidden flex-shrink-0 border border-white/5">
                        <img src={page.thumbnail} alt="" className="w-full h-full object-cover object-top" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <span className="text-white text-sm font-medium truncate block">
                          {language === 'fr' ? page.titleFr : page.titleEn}
                        </span>
                      </div>
                      <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button onClick={() => moveUp(idx)} disabled={idx === 0}
                          className="p-1 text-white/25 hover:text-white disabled:opacity-20 rounded hover:bg-white/5">
                          <ChevronUp className="w-4 h-4" strokeWidth={1.5} />
                        </button>
                        <button onClick={() => moveDown(idx)} disabled={idx === orderedPages.length - 1}
                          className="p-1 text-white/25 hover:text-white disabled:opacity-20 rounded hover:bg-white/5">
                          <ChevronDown className="w-4 h-4" strokeWidth={1.5} />
                        </button>
                        <button onClick={() => removePage(idx)}
                          className="p-1 text-white/25 hover:text-red-400 rounded hover:bg-white/5">
                          <X className="w-4 h-4" strokeWidth={1.5} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
                {orderedPages.length === 0 && (
                  <div className="text-center py-10 text-white/25 text-sm">
                    {t('All pages removed. Go back to select pages.', 'Toutes les pages retirees.')}
                  </div>
                )}
              </div>

              <div className="lg:col-span-2">
                <div className="sticky top-24">
                  <PreviewPanel pages={orderedPages} showComposition={false} />
                </div>
              </div>
            </div>

            <div className="flex justify-between mt-8">
              <Button variant="ghost" onClick={() => setStep(0)} className="text-white/40 hover:text-white hover:bg-white/5 rounded-xl h-11">
                <ArrowLeft className="w-4 h-4 mr-2" strokeWidth={1.5} /> {t('Back', 'Retour')}
              </Button>
              <Button onClick={goToCompose} disabled={orderedPages.length === 0}
                data-testid="next-to-compose"
                className="bg-wine hover:bg-wine-light text-white rounded-xl px-6 h-11">
                {t('Next', 'Suivant')} <ArrowRight className="w-4 h-4 ml-2" strokeWidth={1.5} />
              </Button>
            </div>
          </div>
        )}

        {/* ═══ STEP 3: Compose ═══ */}
        {step === 2 && (
          <div data-testid="step-compose">
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
              <div className="lg:col-span-3">
                <div className="mb-6">
                  <h2 className="font-heading text-2xl font-semibold text-white mb-1">
                    {t('Compose Slides', 'Composer les Diapositives')}
                  </h2>
                  <p className="text-white/40 text-sm">
                    {t(
                      'Choose how your selected pages are combined into slides.',
                      'Choisissez comment vos pages sont combinees en diapositives.'
                    )}
                  </p>
                </div>

                {/* Mode Selector */}
                <div className="space-y-2 mb-8">
                  <h3 className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/25 mb-3">
                    {t('Presentation Mode', 'Mode de Presentation')}
                  </h3>
                  {COMPOSITION_MODES.map((mode) => {
                    const isActive = compositionMode === mode.id;
                    const Icon = mode.icon;
                    return (
                      <button
                        key={mode.id}
                        onClick={() => {
                          setCompositionMode(mode.id);
                          setCustomSlideCount(null);
                        }}
                        data-testid={`compose-mode-${mode.id}`}
                        className={`w-full text-left p-4 rounded-xl border transition-all ${
                          isActive
                            ? 'bg-wine/10 border-wine/30 ring-1 ring-wine/15'
                            : 'bg-[#1A1A1A] border-white/8 hover:border-white/15'
                        }`}
                      >
                        <div className="flex items-start gap-3.5">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                            isActive ? 'bg-wine/20' : 'bg-white/5'
                          }`}>
                            <Icon className={`w-5 h-5 ${isActive ? 'text-wine' : 'text-white/30'}`} strokeWidth={1.5} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <h4 className={`font-heading text-sm font-semibold ${isActive ? 'text-white' : 'text-white/60'}`}>
                                {language === 'fr' ? mode.fr : mode.en}
                              </h4>
                              {isActive && (
                                <div className="w-4 h-4 bg-wine rounded-full flex items-center justify-center">
                                  <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />
                                </div>
                              )}
                            </div>
                            <p className="text-white/30 text-xs mt-0.5">
                              {language === 'fr' ? mode.descFr : mode.descEn}
                            </p>
                          </div>
                          <div className="text-right flex-shrink-0">
                            <span className="text-white/20 text-xs">
                              {mode.id === 'detailed' ? orderedPages.length : mode.id === 'condensed' ? Math.ceil(orderedPages.length / 3) : Math.ceil(orderedPages.length / 4)} {t('slides', 'diapo.')}
                            </span>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>

                {/* Custom slide count (for condensed/executive) */}
                {compositionMode !== 'detailed' && orderedPages.length > 1 && (
                  <div className="bg-[#1A1A1A] border border-white/10 rounded-xl p-5">
                    <h3 className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/25 mb-3">
                      {t('Output Slides', 'Diapositives en Sortie')}
                    </h3>
                    <p className="text-white/30 text-xs mb-4">
                      {t(
                        'Optionally set a custom number of output slides. The system will intelligently group pages.',
                        'Definissez optionnellement le nombre de diapositives. Le systeme regroupera les pages.'
                      )}
                    </p>
                    <div className="flex items-center gap-4">
                      <button
                        onClick={() => setCustomSlideCount(prev => Math.max(1, (prev || getOutputSlideCount()) - 1))}
                        data-testid="slide-count-minus"
                        className="w-10 h-10 rounded-lg bg-white/5 hover:bg-white/10 flex items-center justify-center text-white/50 hover:text-white transition-colors"
                      >
                        <Minus className="w-4 h-4" strokeWidth={2} />
                      </button>
                      <div className="flex-1 text-center">
                        <span data-testid="slide-count-display" className="font-heading text-3xl font-bold text-green-400">
                          {customSlideCount || getOutputSlideCount()}
                        </span>
                        <span className="text-white/20 text-sm ml-2">{t('slides', 'diapositives')}</span>
                      </div>
                      <button
                        onClick={() => setCustomSlideCount(prev => Math.min(orderedPages.length, (prev || getOutputSlideCount()) + 1))}
                        data-testid="slide-count-plus"
                        className="w-10 h-10 rounded-lg bg-white/5 hover:bg-white/10 flex items-center justify-center text-white/50 hover:text-white transition-colors"
                      >
                        <Plus className="w-4 h-4" strokeWidth={2} />
                      </button>
                    </div>
                    <div className="flex justify-between mt-3 text-[10px] text-white/15">
                      <span>{t('Min: 1', 'Min : 1')}</span>
                      <span>{t(`Max: ${orderedPages.length}`, `Max : ${orderedPages.length}`)}</span>
                    </div>
                    {customSlideCount && (
                      <button
                        onClick={() => setCustomSlideCount(null)}
                        className="mt-3 text-xs text-wine/60 hover:text-wine transition-colors"
                      >
                        {t('Reset to default', 'Reinitialiser')}
                      </button>
                    )}
                  </div>
                )}

                {/* Summary */}
                <div className="mt-6 p-4 bg-[#111] border border-white/5 rounded-xl">
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <span className="text-2xl font-bold text-green-400">{orderedPages.length + librarySlides.length}</span>
                      <p className="text-[10px] text-white/25 mt-0.5">{t('Items', 'Elements')}</p>
                    </div>
                    <div>
                      <span className="text-[10px] text-white/15">&rarr;</span>
                    </div>
                    <div>
                      <span className="text-2xl font-bold text-green-400">{getOutputSlideCount() + librarySlides.length}</span>
                      <p className="text-[10px] text-white/25 mt-0.5">{t('Slides', 'Diapositives')}</p>
                    </div>
                  </div>
                </div>

                {/* ─── Add Slide from Library ─── */}
                <div className="mt-6">
                  <h3 className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/25 mb-3">
                    {t('Add Slide', 'Ajouter une Diapositive')}
                  </h3>

                  {/* Library slides added */}
                  {librarySlides.length > 0 && (
                    <div className="space-y-1.5 mb-3">
                      {librarySlides.map((ls, idx) => (
                        <div key={idx} data-testid={`library-slide-${idx}`}
                          className="flex items-center gap-3 bg-[#1A1A1A] border border-blue-500/20 rounded-xl p-2.5">
                          <div className="w-12 h-8 rounded overflow-hidden flex-shrink-0 bg-[#111] border border-white/5">
                            {['png', 'jpg', 'jpeg'].includes(ls.asset.file_type) ? (
                              <img src={libraryApi.fileUrl(ls.asset.id)} alt="" className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <FileText className="w-4 h-4 text-white/20" strokeWidth={1.5} />
                              </div>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <span className="text-white text-sm font-medium truncate block">{ls.title}</span>
                            <span className="text-blue-400/50 text-[10px] uppercase">{ls.mode === 'visual' ? t('Full Visual', 'Visuel Complet') : t('Content Slide', 'Diapositive Contenu')}</span>
                          </div>
                          <button onClick={() => removeLibrarySlide(idx)}
                            className="p-1 text-white/25 hover:text-red-400 rounded hover:bg-white/5">
                            <X className="w-4 h-4" strokeWidth={1.5} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  <button
                    onClick={() => setShowLibraryPicker(true)}
                    data-testid="add-from-library-btn"
                    className="w-full p-3 rounded-xl border border-dashed border-white/10 hover:border-wine/30 text-white/40 hover:text-wine flex items-center justify-center gap-2 transition-colors"
                  >
                    <FolderOpen className="w-4 h-4" strokeWidth={1.5} />
                    <span className="text-sm font-medium">{t('Add from Library', 'Ajouter depuis la Bibliotheque')}</span>
                  </button>
                </div>

                {/* Library Picker Modal */}
                {showLibraryPicker && (
                  <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div data-testid="library-picker-modal" className="bg-[#1A1A1A] border border-white/10 rounded-2xl w-full max-w-2xl max-h-[80vh] flex flex-col">
                      <div className="flex items-center justify-between px-5 py-4 border-b border-white/5">
                        <div className="flex items-center gap-2">
                          {pickerFolderId && (
                            <button onClick={async () => {
                              setPickerFolderId(null);
                              try { const a = await libraryApi.list(''); setLibraryAssets(a); } catch {}
                            }} className="p-1 text-white/30 hover:text-white rounded hover:bg-white/5">
                              <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
                            </button>
                          )}
                          <h2 className="font-heading text-lg font-semibold text-white">
                            {pickerFolderId
                              ? pickerFolders.find(f => f.id === pickerFolderId)?.name || t('Folder', 'Dossier')
                              : t('Select from Library', 'Selectionner depuis la Bibliotheque')}
                          </h2>
                        </div>
                        <button onClick={() => { setShowLibraryPicker(false); setPickerFolderId(null); }}
                          className="p-1.5 text-white/30 hover:text-white rounded-lg hover:bg-white/5">
                          <X className="w-5 h-5" strokeWidth={1.5} />
                        </button>
                      </div>

                      {/* Slide mode toggle */}
                      <div className="px-5 py-3 border-b border-white/5 flex items-center gap-2">
                        <span className="text-xs text-white/40 mr-2">{t('Insert as:', 'Inserer comme :')}</span>
                        <button
                          onClick={() => setLibrarySlideMode('visual')}
                          data-testid="lib-mode-visual"
                          className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                            librarySlideMode === 'visual' ? 'bg-wine text-white' : 'bg-white/5 text-white/40 hover:text-white'
                          }`}
                        >
                          {t('Full Visual Slide', 'Diapositive Visuelle')}
                        </button>
                        <button
                          onClick={() => setLibrarySlideMode('content')}
                          data-testid="lib-mode-content"
                          className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                            librarySlideMode === 'content' ? 'bg-wine text-white' : 'bg-white/5 text-white/40 hover:text-white'
                          }`}
                        >
                          {t('Content Slide', 'Diapositive Contenu')}
                        </button>
                      </div>

                      {/* Content: folders + assets */}
                      <div className="flex-1 overflow-y-auto p-5">
                        {/* Folder list at root */}
                        {!pickerFolderId && pickerFolders.length > 0 && (
                          <div className="mb-4">
                            <h3 className="text-[10px] font-bold uppercase tracking-[0.15em] text-white/25 mb-2">{t('Folders', 'Dossiers')}</h3>
                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-4">
                              {pickerFolders.map((folder) => (
                                <button key={folder.id} onClick={async () => {
                                  setPickerFolderId(folder.id);
                                  try { const a = await libraryApi.list(folder.id); setLibraryAssets(a); } catch {}
                                }}
                                  data-testid={`picker-folder-${folder.id}`}
                                  className="flex items-center gap-2 p-3 bg-white/5 rounded-xl border border-white/8 hover:border-wine/30 text-left transition-all">
                                  <FolderOpen className="w-4 h-4 text-wine flex-shrink-0" strokeWidth={1.5} />
                                  <div className="flex-1 min-w-0">
                                    <span className="text-white text-xs font-medium truncate block">{folder.name}</span>
                                    <span className="text-white/25 text-[10px]">{folder.asset_count || 0} {t('files', 'fichiers')}</span>
                                  </div>
                                </button>
                              ))}
                            </div>
                            {libraryAssets.length > 0 && (
                              <h3 className="text-[10px] font-bold uppercase tracking-[0.15em] text-white/25 mb-2">{t('Unfiled Assets', 'Assets Non-Classes')}</h3>
                            )}
                          </div>
                        )}

                        {/* Asset grid */}
                        {libraryAssets.length === 0 && pickerFolders.length === 0 ? (
                          <div className="text-center py-12">
                            <FolderOpen className="w-10 h-10 text-white/15 mx-auto mb-3" strokeWidth={1.5} />
                            <p className="text-white/40 text-sm">{t('Library is empty.', 'La bibliotheque est vide.')}</p>
                            <Link to="/marcom/library" className="text-wine text-sm mt-2 inline-block hover:underline">
                              {t('Upload assets', 'Telecharger des assets')}
                            </Link>
                          </div>
                        ) : libraryAssets.length === 0 && pickerFolderId ? (
                          <div className="text-center py-8">
                            <p className="text-white/30 text-sm">{t('This folder is empty.', 'Ce dossier est vide.')}</p>
                          </div>
                        ) : (
                          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                            {libraryAssets.map((asset) => {
                              const alreadyAdded = librarySlides.some(ls => ls.asset.id === asset.id);
                              return (
                                <button
                                  key={asset.id}
                                  onClick={() => !alreadyAdded && addLibrarySlide(asset)}
                                  disabled={alreadyAdded}
                                  data-testid={`pick-asset-${asset.id}`}
                                  className={`text-left rounded-xl border overflow-hidden transition-all ${
                                    alreadyAdded
                                      ? 'border-wine/30 opacity-50 cursor-not-allowed'
                                      : 'border-white/8 hover:border-wine/30 cursor-pointer'
                                  }`}
                                >
                                  <div className="aspect-[16/10] bg-[#111] relative overflow-hidden">
                                    {['png', 'jpg', 'jpeg'].includes(asset.file_type) ? (
                                      <img src={libraryApi.fileUrl(asset.id)} alt={asset.title}
                                        className="w-full h-full object-cover" />
                                    ) : (
                                      <div className="w-full h-full flex items-center justify-center">
                                        <FileText className="w-8 h-8 text-white/15" strokeWidth={1.5} />
                                      </div>
                                    )}
                                    {alreadyAdded && (
                                      <div className="absolute top-1.5 right-1.5 w-5 h-5 bg-wine rounded-full flex items-center justify-center">
                                        <Check className="w-3 h-3 text-white" strokeWidth={2.5} />
                                      </div>
                                    )}
                                  </div>
                                  <div className="p-2">
                                    <h4 className="text-xs font-medium text-white truncate">{asset.title}</h4>
                                    <span className="text-[10px] text-white/25 uppercase">{asset.file_type}</span>
                                  </div>
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* ─── Personalize This Marcom ─── */}
                <div className="mt-6">
                  <h3 className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/25 mb-3">
                    {t('Personalization', 'Personnalisation')}
                  </h3>

                  {isPersonalized && !showPersonalize && (
                    <div data-testid="personalize-summary" className="bg-[#1A1A1A] border border-wine/20 rounded-xl p-4 mb-3">
                      <div className="flex items-center gap-3">
                        {clientLogoPreview && (
                          <img src={clientLogoPreview} alt="" className="w-10 h-10 rounded-lg object-contain bg-white/5 border border-white/10" />
                        )}
                        <div className="flex-1 min-w-0">
                          <span className="text-white text-sm font-medium block truncate">{clientName || t('Custom Branding', 'Image de Marque Personnalisee')}</span>
                          <span className="text-wine/60 text-xs">{t('Personalized', 'Personnalise')}</span>
                        </div>
                        <button onClick={() => setShowPersonalize(true)} className="text-xs text-wine hover:text-wine-light transition-colors">
                          {t('Edit', 'Modifier')}
                        </button>
                      </div>
                    </div>
                  )}

                  {!showPersonalize && !isPersonalized && (
                    <button
                      onClick={() => setShowPersonalize(true)}
                      data-testid="personalize-btn"
                      className="w-full p-3 rounded-xl border border-dashed border-white/10 hover:border-wine/30 text-white/40 hover:text-wine flex items-center justify-center gap-2 transition-colors"
                    >
                      <User className="w-4 h-4" strokeWidth={1.5} />
                      <span className="text-sm font-medium">{t('Personalize This Marcom', 'Personnaliser ce Marcom')}</span>
                    </button>
                  )}

                  {showPersonalize && (
                    <div data-testid="personalize-panel" className="bg-[#1A1A1A] border border-white/10 rounded-xl p-5 space-y-4">
                      <div className="flex items-center justify-between mb-1">
                        <h4 className="text-white font-medium text-sm">{t('Client Branding', 'Image de Marque Client')}</h4>
                        <button onClick={() => setShowPersonalize(false)} className="p-1 text-white/25 hover:text-white rounded">
                          <X className="w-4 h-4" strokeWidth={1.5} />
                        </button>
                      </div>

                      {/* Client Name */}
                      <div>
                        <label className="text-white/60 text-xs font-medium block mb-1">{t('Client Name', 'Nom du Client')}</label>
                        <Input value={clientName} onChange={(e) => setClientName(e.target.value)}
                          data-testid="client-name-input"
                          placeholder={t('e.g., Restaurant Le Chateau', 'ex., Restaurant Le Chateau')}
                          className="bg-white/5 border-white/10 text-white placeholder:text-white/25 h-10 rounded-lg" />
                      </div>

                      {/* Logo Upload */}
                      <div>
                        <label className="text-white/60 text-xs font-medium block mb-1">{t('Client Logo', 'Logo du Client')}</label>
                        <div className="flex items-center gap-3">
                          {clientLogoPreview ? (
                            <div className="relative">
                              <img src={clientLogoPreview} alt="" className="w-14 h-14 rounded-lg object-contain bg-white/5 border border-white/10" />
                              <button onClick={() => { setClientLogoFile(null); setClientLogoPreview(''); setClientLogoId(''); }}
                                className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500/80 rounded-full flex items-center justify-center">
                                <X className="w-3 h-3 text-white" strokeWidth={2} />
                              </button>
                            </div>
                          ) : (
                            <label data-testid="logo-upload-zone"
                              className="flex items-center gap-2 px-4 py-2.5 rounded-lg border border-dashed border-white/15 hover:border-wine/30 cursor-pointer transition-colors">
                              <Upload className="w-4 h-4 text-white/30" strokeWidth={1.5} />
                              <span className="text-white/40 text-xs">{t('Upload Logo (PNG/JPG)', 'Telecharger Logo (PNG/JPG)')}</span>
                              <input type="file" accept=".png,.jpg,.jpeg" className="hidden"
                                data-testid="logo-file-input"
                                onChange={handleLogoUpload} />
                            </label>
                          )}
                          {uploadingLogo && <div className="w-4 h-4 border-2 border-wine border-t-transparent rounded-full animate-spin" />}
                        </div>
                      </div>

                      {/* Brand Color */}
                      <div>
                        <label className="text-white/60 text-xs font-medium block mb-1">
                          {t('Brand Color', 'Couleur de Marque')} <span className="text-white/20">({t('optional', 'optionnel')})</span>
                        </label>
                        <div className="flex items-center gap-2">
                          <input type="color" value={brandColor || '#6B2D3E'}
                            onChange={(e) => setBrandColor(e.target.value)}
                            data-testid="brand-color-input"
                            className="w-10 h-10 rounded-lg border border-white/10 cursor-pointer bg-transparent" />
                          <Input value={brandColor} onChange={(e) => setBrandColor(e.target.value)}
                            placeholder="#6B2D3E"
                            className="bg-white/5 border-white/10 text-white placeholder:text-white/25 h-10 rounded-lg flex-1 font-mono text-sm" />
                          {brandColor && (
                            <button onClick={() => setBrandColor('')} className="text-xs text-white/25 hover:text-white/50">
                              {t('Clear', 'Effacer')}
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Location Name */}
                      <div>
                        <label className="text-white/60 text-xs font-medium block mb-1">
                          {t('Location Name', 'Nom de l\'Emplacement')} <span className="text-white/20">({t('optional', 'optionnel')})</span>
                        </label>
                        <Input value={locationName} onChange={(e) => setLocationName(e.target.value)}
                          data-testid="location-name-input"
                          placeholder={t('e.g., Downtown Montreal', 'ex., Centre-Ville Montreal')}
                          className="bg-white/5 border-white/10 text-white placeholder:text-white/25 h-10 rounded-lg" />
                      </div>

                      {/* Prepared For */}
                      <div>
                        <label className="text-white/60 text-xs font-medium block mb-1">
                          {t('Prepared For', 'Prepare Pour')} <span className="text-white/20">({t('optional', 'optionnel')})</span>
                        </label>
                        <Input value={preparedFor} onChange={(e) => setPreparedFor(e.target.value)}
                          data-testid="prepared-for-input"
                          placeholder={t('e.g., Jean-Pierre Tremblay, GM', 'ex., Jean-Pierre Tremblay, DG')}
                          className="bg-white/5 border-white/10 text-white placeholder:text-white/25 h-10 rounded-lg" />
                      </div>

                      {/* Reset */}
                      {isPersonalized && (
                        <button onClick={resetPersonalization} data-testid="reset-branding-btn"
                          className="w-full text-center text-xs text-white/30 hover:text-red-400 py-2 transition-colors">
                          {t('Reset to Default Branding', 'Reinitialiser l\'image de marque par defaut')}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
              <div className="lg:col-span-2">
                <div className="sticky top-24">
                  <PreviewPanel pages={orderedPages} showComposition={true} />
                </div>
              </div>
            </div>

            <div className="flex justify-between mt-8">
              <Button variant="ghost" onClick={() => setStep(1)} className="text-white/40 hover:text-white hover:bg-white/5 rounded-xl h-11">
                <ArrowLeft className="w-4 h-4 mr-2" strokeWidth={1.5} /> {t('Back', 'Retour')}
              </Button>
              <Button onClick={goToSecurity} data-testid="next-to-security"
                className="bg-wine hover:bg-wine-light text-white rounded-xl px-6 h-11">
                {t('Next', 'Suivant')} <ArrowRight className="w-4 h-4 ml-2" strokeWidth={1.5} />
              </Button>
            </div>
          </div>
        )}

        {/* ═══ STEP 4: Security ═══ */}
        {step === 3 && (
          <div data-testid="step-security" className="max-w-lg mx-auto">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-wine/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Shield className="w-8 h-8 text-wine" strokeWidth={1.5} />
              </div>
              <h2 className="font-heading text-2xl font-semibold text-white mb-1">
                {t('Security Settings', 'Parametres de Securite')}
              </h2>
              <p className="text-white/40 text-sm">
                {t('Protect your Marcom with a password.', 'Protegez votre Marcom avec un mot de passe.')}
              </p>
            </div>

            <div className="bg-[#1A1A1A] border border-white/10 rounded-xl p-6 space-y-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {usePassword ? <Lock className="w-5 h-5 text-wine" strokeWidth={1.5} /> : <Unlock className="w-5 h-5 text-white/30" strokeWidth={1.5} />}
                  <span className="text-white font-medium">
                    {t('Password Protect This Marcom?', 'Proteger ce Marcom par mot de passe?')}
                  </span>
                </div>
                <button
                  onClick={() => setUsePassword(!usePassword)}
                  data-testid="password-toggle"
                  className={`relative w-12 h-7 rounded-full transition-colors ${usePassword ? 'bg-wine' : 'bg-white/10'}`}
                >
                  <div className={`absolute top-0.5 w-6 h-6 bg-white rounded-full shadow transition-transform ${usePassword ? 'translate-x-5' : 'translate-x-0.5'}`} />
                </button>
              </div>

              {usePassword && (
                <>
                  <div className="pt-2 border-t border-white/5 space-y-4">
                    <p className="text-white/40 text-xs">
                      {t('Leave blank for auto-generated password, or enter your own.', 'Laissez vide pour un mot de passe auto-genere, ou entrez le votre.')}
                    </p>
                    <div>
                      <label className="text-white/60 text-xs font-medium block mb-1.5">{t('Password', 'Mot de passe')}</label>
                      <Input type={showPassword ? 'text' : 'password'} value={customPassword}
                        onChange={(e) => setCustomPassword(e.target.value)} data-testid="marco-password-input"
                        placeholder={t('Auto-generated if empty', 'Auto-genere si vide')}
                        className="bg-white/5 border-white/10 text-white placeholder:text-white/25 h-11 rounded-lg" />
                    </div>
                    {customPassword && (
                      <div>
                        <label className="text-white/60 text-xs font-medium block mb-1.5">{t('Confirm Password', 'Confirmer le mot de passe')}</label>
                        <Input type={showPassword ? 'text' : 'password'} value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)} data-testid="marco-confirm-password"
                          placeholder={t('Re-enter password', 'Ressaisir le mot de passe')}
                          className="bg-white/5 border-white/10 text-white placeholder:text-white/25 h-11 rounded-lg" />
                      </div>
                    )}
                    <button onClick={() => setShowPassword(!showPassword)}
                      className="text-xs text-white/30 hover:text-white/50 transition-colors">
                      {showPassword ? t('Hide passwords', 'Masquer') : t('Show passwords', 'Afficher')}
                    </button>
                  </div>

                  <div className="pt-4 border-t border-white/5">
                    <div className="flex items-center gap-2 mb-2">
                      <Calendar className="w-4 h-4 text-white/30" strokeWidth={1.5} />
                      <label className="text-white/60 text-xs font-medium">{t('Expiry Date (Optional)', 'Date d\'expiration (Optionnel)')}</label>
                    </div>
                    <Input type="date" value={passwordExpiry} onChange={(e) => setPasswordExpiry(e.target.value)}
                      data-testid="marco-expiry-input"
                      className="bg-white/5 border-white/10 text-white h-11 rounded-lg w-full" />
                    <p className="text-white/25 text-xs mt-1.5">{t('Leave empty for no expiration.', 'Laissez vide pour aucune expiration.')}</p>
                  </div>
                </>
              )}
            </div>

            {error && <p data-testid="security-error" className="text-red-400 text-sm mt-4">{error}</p>}

            <div className="flex justify-between mt-8">
              <Button variant="ghost" onClick={() => setStep(2)} className="text-white/40 hover:text-white hover:bg-white/5 rounded-xl h-11">
                <ArrowLeft className="w-4 h-4 mr-2" strokeWidth={1.5} /> {t('Back', 'Retour')}
              </Button>
              <Button onClick={goToPreview} data-testid="next-to-preview"
                className="bg-wine hover:bg-wine-light text-white rounded-xl px-6 h-11">
                {t('Preview', 'Apercu')} <ArrowRight className="w-4 h-4 ml-2" strokeWidth={1.5} />
              </Button>
            </div>
          </div>
        )}


        {/* ═══ STEP 5: Preview ═══ */}
        {step === 4 && (
          <div data-testid="step-preview" className="max-w-5xl mx-auto">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-wine/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Eye className="w-8 h-8 text-wine" strokeWidth={1.5} />
              </div>
              <h2 className="font-heading text-2xl font-semibold text-white mb-1">
                {t('Presentation Preview', 'Apercu de la Presentation')}
              </h2>
              <p className="text-white/50 text-sm">
                {t('Review your slides before generating. Go back to make changes.', 'Verifiez vos diapositives avant de generer. Retournez pour modifier.')}
              </p>
            </div>

            {/* Preview Info Bar */}
            <div className="flex items-center justify-between p-3 bg-[#1A1A1A] border border-white/10 rounded-xl mb-6">
              <div className="flex items-center gap-4">
                <span className="text-white/50 text-xs">{t('Mode:', 'Mode :')} <span className="text-white font-medium">{COMPOSITION_MODES.find(m => m.id === compositionMode)?.[language === 'fr' ? 'fr' : 'en'] || compositionMode}</span></span>
                <span className="text-white/50 text-xs">{t('Slides:', 'Diapositives :')} <span className="text-white font-medium">{getOutputSlideCount() + librarySlides.length + 1}</span></span>
                {isPersonalized && clientName && (
                  <span className="text-white/50 text-xs">{t('Brand:', 'Marque :')} <span className="text-wine font-medium">{clientName}</span></span>
                )}
              </div>
              {usePassword && <Lock className="w-4 h-4 text-wine/60" strokeWidth={1.5} />}
            </div>

            {/* Slide Preview Grid */}
            <div className="space-y-4">
              {/* Cover Slide */}
              <div className="bg-[#111] border border-white/10 rounded-xl overflow-hidden">
                <div className="flex items-center gap-3 px-4 py-2.5 bg-[#1A1A1A] border-b border-white/5">
                  <div className="w-6 h-6 bg-wine/20 rounded-md flex items-center justify-center">
                    <span className="text-wine text-xs font-bold">C</span>
                  </div>
                  <span className="text-white text-sm font-medium">{t('Cover Slide', 'Diapositive de Couverture')}</span>
                </div>
                <div className="p-5 flex items-center gap-6">
                  {clientLogoPreview ? (
                    <img src={clientLogoPreview} alt="" className="h-12 w-auto object-contain opacity-80" />
                  ) : (
                    <img src={ASSETS.LOGO} alt="" className="h-12 w-auto opacity-40" />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-white/80 text-lg font-heading font-semibold truncate">{name || t('Untitled Presentation', 'Presentation Sans Titre')}</p>
                    <p className="text-white/30 text-xs mt-1">{t('Prepared by', 'Prepare par')} YUL On Tap</p>
                    {preparedFor && <p className="text-wine/70 text-xs mt-0.5">{t('For', 'Pour')}: {preparedFor}</p>}
                  </div>
                </div>
              </div>

              {/* Content Slides */}
              {(() => {
                const groups = getSlideGroups();
                return groups.map((group, idx) => (
                  <div key={`slide-${idx}`} className="bg-[#111] border border-white/10 rounded-xl overflow-hidden">
                    <div className="flex items-center gap-3 px-4 py-2.5 bg-[#1A1A1A] border-b border-white/5">
                      <div className="w-6 h-6 bg-white/10 rounded-md flex items-center justify-center">
                        <span className="text-white/60 text-xs font-bold">{idx + 1}</span>
                      </div>
                      <span className="text-white text-sm font-medium truncate">
                        {group.map(p => language === 'fr' ? p.titleFr : p.titleEn).join(' + ')}
                      </span>
                      {group.length > 1 && <span className="text-white/20 text-[10px] ml-auto">{group.length} {t('pages', 'pages')}</span>}
                    </div>
                    <div className={`grid ${group.length === 1 ? 'grid-cols-1' : group.length === 2 ? 'grid-cols-2' : 'grid-cols-3'} gap-px bg-white/5`}>
                      {group.map((page) => (
                        <div key={page.id} className="relative bg-[#0D0D0D]">
                          <div className="aspect-[16/9] overflow-hidden">
                            <img src={page.thumbnail} alt="" className="w-full h-full object-cover object-top" />
                          </div>
                          <div className="px-3 py-2">
                            <p className="text-white/60 text-xs font-medium truncate">{language === 'fr' ? page.titleFr : page.titleEn}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ));
              })()}

              {/* Library Slides */}
              {librarySlides.map((ls, idx) => (
                <div key={`lib-${idx}`} className="bg-[#111] border border-white/10 rounded-xl overflow-hidden">
                  <div className="flex items-center gap-3 px-4 py-2.5 bg-[#1A1A1A] border-b border-white/5">
                    <div className="w-6 h-6 bg-wine/10 rounded-md flex items-center justify-center">
                      <Image className="w-3 h-3 text-wine" strokeWidth={2} />
                    </div>
                    <span className="text-white text-sm font-medium truncate">{ls.title}</span>
                    <span className="text-white/15 text-[10px] ml-auto uppercase">{ls.mode === 'visual' ? t('Full Visual', 'Visuel Complet') : t('Content', 'Contenu')}</span>
                  </div>
                  <div className="p-3 flex items-center gap-4">
                    {ls.asset?.file_url && ['png', 'jpg', 'jpeg'].includes(ls.asset.file_type) ? (
                      <img src={ls.asset.file_url || libraryApi.fileUrl(ls.asset.id)} alt="" className="h-20 w-auto rounded-lg object-cover" />
                    ) : (
                      <div className="h-20 w-20 bg-white/5 rounded-lg flex items-center justify-center">
                        <FileText className="w-6 h-6 text-white/15" strokeWidth={1.5} />
                      </div>
                    )}
                    {ls.description && <p className="text-white/40 text-xs flex-1 line-clamp-3">{ls.description}</p>}
                  </div>
                </div>
              ))}
            </div>

            {/* Actions */}
            <div className="flex justify-between mt-8">
              <Button variant="ghost" onClick={() => setStep(3)} className="text-white/40 hover:text-white hover:bg-white/5 rounded-xl h-11">
                <ArrowLeft className="w-4 h-4 mr-2" strokeWidth={1.5} /> {t('Back to Security', 'Retour a Securite')}
              </Button>
              <Button onClick={goToGenerate} data-testid="next-to-generate"
                className="bg-wine hover:bg-wine-light text-white rounded-xl px-6 h-11">
                {t('Generate', 'Generer')} <ArrowRight className="w-4 h-4 ml-2" strokeWidth={1.5} />
              </Button>
            </div>
          </div>
        )}

        {/* ═══ STEP 6: Name & Generate ═══ */}
        {step === 5 && (
          <div data-testid="step-generate" className="max-w-lg mx-auto">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-wine/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Save className="w-8 h-8 text-wine" strokeWidth={1.5} />
              </div>
              <h2 className="font-heading text-2xl font-semibold text-white mb-1">
                {t('Name & Generate', 'Nommer et Generer')}
              </h2>
              <p className="text-white/40 text-sm">
                {t(
                  `${getOutputSlideCount() + librarySlides.length} slide${(getOutputSlideCount() + librarySlides.length) > 1 ? 's' : ''} ready. Give it a name.`,
                  `${getOutputSlideCount() + librarySlides.length} diapositive${(getOutputSlideCount() + librarySlides.length) > 1 ? 's' : ''} prete${(getOutputSlideCount() + librarySlides.length) > 1 ? 's' : ''}. Donnez-lui un nom.`
                )}
              </p>
            </div>

            <div className="bg-[#1A1A1A] border border-white/10 rounded-xl p-6">
              <label className="text-white/60 text-sm font-medium block mb-2">{t('Marcom Name', 'Nom du Marcom')}</label>
              <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="marco-name-input"
                placeholder={t('e.g., Q2 Pitch Deck', 'ex., Presentation T2')}
                className="bg-white/5 border-white/10 text-white placeholder:text-white/25 h-12 rounded-lg text-base" autoFocus />

              <div className="mt-5 pt-5 border-t border-white/5">
                <h4 className="text-[10px] font-bold uppercase tracking-[0.15em] text-white/25 mb-3">{t('Summary', 'Resume')}</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-white/40">{t('Pages', 'Pages')}</span>
                    <span className="text-white">{orderedPages.length}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-white/40">{t('Mode', 'Mode')}</span>
                    <span className="text-white capitalize">{compositionMode}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-white/40">{t('Output Slides', 'Diapositives')}</span>
                    <span className="text-wine font-semibold">{getOutputSlideCount() + librarySlides.length}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-white/40">{t('Password', 'Mot de passe')}</span>
                    <span className="text-white">
                      {usePassword
                        ? (customPassword ? t('Custom', 'Personnalise') : t('Auto-generated', 'Auto-genere'))
                        : t('None', 'Aucun')
                      }
                    </span>
                  </div>
                  {passwordExpiry && (
                    <div className="flex justify-between">
                      <span className="text-white/40">{t('Expires', 'Expire')}</span>
                      <span className="text-white">{passwordExpiry}</span>
                    </div>
                  )}
                  {isPersonalized && (
                    <div className="flex justify-between">
                      <span className="text-white/40">{t('Personalized for', 'Personnalise pour')}</span>
                      <span className="text-wine font-medium">{clientName || t('Custom Branding', 'Marque Personnalisee')}</span>
                    </div>
                  )}
                </div>

                <div className="mt-4 pt-4 border-t border-white/5 space-y-1.5">
                  {orderedPages.map((page, idx) => (
                    <div key={page.id} className="flex items-center gap-2">
                      <span className="text-wine text-xs font-semibold w-4">{idx + 1}.</span>
                      <span className="text-white/50 text-sm">{language === 'fr' ? page.titleFr : page.titleEn}</span>
                    </div>
                  ))}
                </div>
              </div>

              {error && <p data-testid="generate-error" className="text-red-400 text-sm mt-4">{error}</p>}

              <Button onClick={handleGenerate} disabled={saving} data-testid="generate-marco-btn"
                className="w-full bg-wine hover:bg-wine-light text-white rounded-xl h-12 text-base font-medium mt-6">
                {saving ? (
                  <span className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    {t('Generating...', 'Generation...')}
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Save className="w-4 h-4" strokeWidth={1.5} />
                    {t('Generate Marcom', 'Generer le Marcom')}
                  </span>
                )}
              </Button>
            </div>

            <div className="flex justify-start mt-6">
              <Button variant="ghost" onClick={() => setStep(4)} className="text-white/40 hover:text-white hover:bg-white/5 rounded-xl h-11">
                <ArrowLeft className="w-4 h-4 mr-2" strokeWidth={1.5} /> {t('Back to Preview', 'Retour a l\'Apercu')}
              </Button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default MarcoCreate;
